import { buffers, eventChannel, END } from 'redux-saga';
import Auth from '@aws-amplify/auth';
import mockedData from 'common/mockedData';
import Logger from 'utils/logger';
import { withoutTrailingSlash } from 'utils/path';
import runningInDev from 'utils/runningInDev';
import configuredStore from 'common/configStore';
import { selectAccessToken, selectCaseRegionTemp, selectCountryCode } from 'common/selectors';
import { logout } from 'features/auth/redux/logout';
import T from 'i18n';

export const getAccessToken = () => {
  return selectAccessToken(configuredStore.store.getState());
};

// eslint-disable-next-line no-undef
const unifiedCookieEnabled = process.env.REACT_APP_UNIFIED_COOKIE_ENABLED === 'true';

const getCountryCode = () => {
  const tempcaseRegion = selectCaseRegionTemp(configuredStore.store.getState());
  if (tempcaseRegion) return tempcaseRegion;
  else return selectCountryCode(configuredStore.store.getState());
};

const createErrorFromResponse = ({ status, statusText }, body) => ({
  status,
  statusText,
  body,
});

let isCheckingAuthOn401 = false;

const checkAuthOn401 = () => {
  if (isCheckingAuthOn401) return;
  isCheckingAuthOn401 = true;

  // First, try Cognito session refresh (backwards compatibility)
  Auth.currentAuthenticatedUser({ bypassCache: true })
    .then(() => {
      isCheckingAuthOn401 = false;
    })
    .catch((err) => {
      isCheckingAuthOn401 = false;
      if (err && err.code === 'NotAuthorizedException') {
        // Cognito session definitively expired/revoked — logout
        try {
          configuredStore.store.dispatch(logout('sessionExpired'));
        } catch (e) {
          Logger.ERROR(e, 'Error dispatching sessionExpired logout after 401');
        }
      } else if (typeof err === 'string') {
        // Amplify throws a string like 'The user is not authenticated' when no
        // Cognito session exists at all (e.g. SSO-only user). Since the 401 means
        // their cookie session also failed, logout is correct here.
        try {
          configuredStore.store.dispatch(logout('sessionExpired'));
        } catch (e) {
          Logger.ERROR(e, 'Error dispatching sessionExpired logout after 401 (no Cognito session)');
        }
      } else {
        // Transient error (network issue, timeout, etc.) — don't force logout.
        // The user may still have a valid session; let them retry on next request.
        Logger.WARN && Logger.WARN('Cognito auth check failed after 401 (transient error, not logging out):', err);
      }
    });
};

const getBody = async (response) => {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') !== -1) {
    try {
      return response.json();
    } catch {
      Logger.ERROR('Wrong content-type header - parsing json failed');
    }
  } else if (contentType && contentType.indexOf('application/pdf') !== -1) {
    try {
      return response.arrayBuffer();
    } catch {
      Logger.ERROR('Wrong content-type header - parsing blob failed');
    }
  } else {
    return response.text();
  }
};

const headersFrom = (headersInit = {}) => new Headers(headersInit);

function _fetch(url, init, processResponse, protectedApiCall = true) {
  const handleError = (error, method) => {
    // eslint-disable-next-line no-undef
    if (runningInDev && process.env.REACT_APP_MOCKED === 'true') {
      if (error) Logger.ERROR('Error fetching', url, error);
      return mockedData(url, method);
    } else {
      return {
        error, //: JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))),
      };
    }
  };

  const code = getCountryCode();
  const accessToken = getAccessToken();

  if (!protectedApiCall || accessToken) {
    const headers = headersFrom(init.headers);
    // Always send Bearer header when accessToken exists — backend prioritises cookie but
    // falls back to Bearer, so both auth methods work during the migration period.
    if (protectedApiCall && accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
    const newInit = {
      ...init,
      headers: headers,
    };
    if (unifiedCookieEnabled) {
      newInit.credentials = 'include'; // Send tv_session cookie when unified cookie is enabled
    }
    // eslint-disable-next-line no-undef
	    return fetch(withoutTrailingSlash(process.env[`REACT_APP_API_${code}`]) + url, newInit)
	      .then(async (response) => {
	        if (processResponse) return processResponse(response);

	        return getBody(response).then((body) => {
	          if (response.ok) {
	            if (response.status !== 204) return body;
	            return;
	          }
	          if (response.status === 401 && protectedApiCall) {
	            checkAuthOn401();
	          }
	          return handleError(
	            createErrorFromResponse(response, body).body ||
	              T.translate('generic.beErrorDefaultMessage'),
	            newInit.method,
	          );
	        });
	      })
      .catch((error) => {
        return handleError(error.message, newInit.method);
      });
  } else {
    return handleError('No access token');
  }
}

function get(url, init = {}, protectedApiCall) {
  const newInit = {
    ...init,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  };
  return _fetch(url, newInit, undefined, protectedApiCall);
}

function post(url, body, options = {}) {
  const newInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    ...options, // Spread additional options like signal
  };
  return _fetch(url, newInit);
}

function postFile(url, body) {
  const newInit = {
    method: 'POST',
    body,
  };
  return _fetch(url, newInit);
}

const fileNameRegex = "filename[^;=\\n]*=(?:(\\\\?['\"])(.*?)\\1|(?:[^\\s]+'.*?')?([^;\\n]*))";
// prettier-ignore
const fileNameEncodedRegex = "filename*[^;=\\n]*=UTF-8''([^;\\n]*)";

function getFile(url, body) {
  const newInit = {
    method: 'GET',
    body,
  };
  const processResponse = (response) => {
    if (response.ok) {
      const content = response.headers.get('content-disposition');
      const fileName = content && content.match(fileNameRegex);
      return response.blob().then((x) => ({
        blob: x,
        fileName:
          fileName &&
          ((fileName.length > 2 && fileName[3]) || (fileName.length > 1 && fileName[2])),
      }));
    } else return response.json().then((x) => ({ error: x || response.status }));
  };
  return _fetch(url, newInit, processResponse);
}

function put(url, body) {
  const newInit = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
  return _fetch(url, newInit);
}

function patch(url, body) {
  const newInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
  return _fetch(url, newInit);
}

function del(url, body) {
  const newInit = body
    ? {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }
    : {
        method: 'DELETE',
      };
  return _fetch(url, newInit);
}

function createUploadFileChannel(method, url, file) {
  const accessToken = getAccessToken();
  const code = getCountryCode();
  return eventChannel((emitter) => {
	    const xhr = new XMLHttpRequest();
    const onProgress = (e) => {
      if (e.lengthComputable) {
        const progress = ((e.loaded / e.total) * 100).toFixed(2);
        emitter({ progress });
      }
    };
    const onFailure = (error) => {
      emitter({ error });
      emitter(END);
    };
    xhr.timeout = 600000;
    xhr.upload.addEventListener('progress', onProgress);
    xhr.upload.addEventListener('error', onFailure);
    xhr.upload.addEventListener('abort', onFailure);
    xhr.onreadystatechange = () => {
      const { readyState, status, responseText } = xhr;
      let body;
      try {
        body = JSON.parse(responseText);
      } catch {
        body = responseText;
      }
	      if (readyState === 4) {
	        if (status === 401) {
	          checkAuthOn401();
	        }
	        if ([200, 201].includes(status)) {
          emitter({ success: true, response: body });
          emitter(END);
        } else if (status === 204) {
          emitter({ success: true });
          emitter(END);
        } else {
          onFailure({ ...createErrorFromResponse(xhr, body) });
        }
      }
    };
    // eslint-disable-next-line no-undef
    xhr.open(method, withoutTrailingSlash(process.env[`REACT_APP_API_${code}`]) + url, true);
    xhr.withCredentials = unifiedCookieEnabled; // Send tv_session cookie with XHR uploads when unified cookie is enabled
    // Always send Bearer — backend prioritises cookie but falls back to Bearer
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    xhr.send(file);
    return () => {
      xhr.upload.removeEventListener('progress', onProgress);
      xhr.upload.removeEventListener('error', onFailure);
      xhr.upload.removeEventListener('abort', onFailure);
      xhr.onreadystatechange = null;
      xhr.abort();
    };
  }, buffers.sliding(2));
}

function createDownloadFileChannel(url, useGivenUrl, options = {}) {
  const successStatuses = [200, 201, 202];
  const processResponse = (xhr) => {
    if (successStatuses.includes(xhr.status)) {
      const content = xhr.getResponseHeader('content-disposition');
      const fileName = content && content.match(fileNameEncodedRegex);
      return {
        blob: xhr.response,
        // prettier-ignore
        fileName:
        fileName && decodeURIComponent(fileName[1]),
      };
    } else if (xhr.status === 0) {
      return 'Possible timeout';
    } else {
      try {
        return xhr.responseText;
      } catch {
        return xhr.response;
      }
    }
  };

  const { method = 'GET', body, headers: requestHeaders = {} } = options || {};
  const accessToken = getAccessToken();
  const code = getCountryCode();

  return eventChannel((emitter) => {
	    const xhr = new XMLHttpRequest();
    const onProgress = (e) => {
      if (e.lengthComputable) {
        const progress = ((e.loaded / e.total) * 100).toFixed(2);
        emitter({ progress });
      }
    };
    const onFailure = (error) => {
      emitter({ error });
      emitter(END);
    };
    xhr.timeout = 600000;
    xhr.addEventListener('progress', onProgress);
    xhr.addEventListener('error', onFailure);
    xhr.addEventListener('abort', onFailure);
    xhr.onreadystatechange = () => {
      const { readyState, status } = xhr;
	      if (readyState === 4) {
	        if (status === 401) {
	          checkAuthOn401();
	        }
	        if (successStatuses.includes(status)) {
          emitter({ success: true, response: processResponse(xhr) });
          emitter(END);
        } else if (status === 204) {
          emitter({ success: true });
          emitter(END);
        } else {
          onFailure({ ...createErrorFromResponse(xhr, processResponse(xhr)) });
        }
      }
    };
    xhr.open(
      method,
      // eslint-disable-next-line no-undef
      withoutTrailingSlash(useGivenUrl ? url : process.env[`REACT_APP_API_${code}`] + url),
      true,
    );
    if (unifiedCookieEnabled) xhr.withCredentials = true; // Send tv_session cookie with XHR downloads
    // Always send Bearer — backend prioritises cookie but falls back to Bearer
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    Object.entries(requestHeaders).forEach(([key, value]) => {
      if (typeof value !== 'undefined') {
        xhr.setRequestHeader(key, value);
      }
    });
    xhr.responseType = 'blob';
    if (typeof body === 'undefined') {
      xhr.send();
    } else {
      xhr.send(body);
    }
    return () => {
      xhr.removeEventListener('progress', onProgress);
      xhr.removeEventListener('error', onFailure);
      xhr.removeEventListener('abort', onFailure);
      xhr.onreadystatechange = null;
      xhr.abort();
    };
  }, buffers.sliding(2));
}

export const getApiBaseUrl = () => {
  const code = getCountryCode();
  // eslint-disable-next-line no-undef
  return withoutTrailingSlash(process.env[`REACT_APP_API_${code}`]);
};

export default {
  get,
  put,
  patch,
  del,
  post,
  postFile,
  getFile,
  createUploadFileChannel,
  createDownloadFileChannel,
  getApiBaseUrl,
};
